/* ============================================================
   CUSTOMISATION: The Rare Steps — product page JS
   Sticky ATC bar (#28), size-chart trigger (#2),
   rating smooth-scroll (#9), mobile description collapse (#23).
   Relies on Dawn's pubsub.js (subscribe/PUB_SUB_EVENTS) + product-form.js.
   ============================================================ */

/* ---------- Utility: debounce ---------- */
function trsDebounce(fn, wait) {
  var t;
  return function () {
    var ctx = this, args = arguments;
    clearTimeout(t);
    t = setTimeout(function () { fn.apply(ctx, args); }, wait);
  };
}

/* ---------- #28 Sticky add-to-cart bar (mobile) ---------- */
if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this._dismissed = false;
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        const sentinel = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!sentinel) return;

        this.observer = new IntersectionObserver(
          ([entry]) => {
            // Reset dismiss when user scrolls back to the original ATC area
            if (entry.isIntersecting) this._dismissed = false;
            const buyBoxAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            const shouldShow = buyBoxAbove && !this._dismissed;
            this.classList.toggle('trs-sticky-atc--visible', shouldShow);
            this.toggleAttribute('aria-hidden', !shouldShow);
          },
          { threshold: 0 }
        );
        this.observer.observe(sentinel);

        const closeBtn = this.querySelector('[data-sticky-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this._dismissed = true;
            this.classList.remove('trs-sticky-atc--visible');
            this.setAttribute('aria-hidden', 'true');
          });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (sectionId !== this.sectionId && sectionId !== this.dataset.section) return;

        const priceEl = this.querySelector('[data-sticky-price]');
        const variantEl = this.querySelector('[data-sticky-variant]');
        const thumbEl = this.querySelector('[data-sticky-thumb]');
        const button = this.querySelector('[data-sticky-submit]');

        if (!variant) {
          if (button) {
            button.setAttribute('disabled', 'disabled');
            button.textContent = window.variantStrings?.unavailable || button.textContent;
          }
          return;
        }

        if (priceEl && typeof variant.price === 'number') {
          priceEl.textContent = this.formatMoney(variant.price);
        }
        if (variantEl) variantEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
        if (thumbEl && variant.featured_media?.preview_image?.src) {
          thumbEl.src = `${variant.featured_media.preview_image.src}&width=88`;
        }
        if (button) {
          if (variant.available) {
            button.removeAttribute('disabled');
            button.textContent = window.variantStrings?.addToCart || button.textContent;
          } else {
            button.setAttribute('disabled', 'disabled');
            button.textContent = window.variantStrings?.soldOut || button.textContent;
          }
        }
      }

      formatMoney(cents) {
        const formatted = (cents / 100).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        return `${this.dataset.currencySymbol || ''}${formatted}`;
      }
    }
  );
}

/* ---------- #12 Require size selection (no default size 3) ---------- */
function trsEnsureSizeError(fieldset) {
  let err = fieldset.parentElement.querySelector('.trs-size-error');
  if (!err) {
    err = document.createElement('p');
    err.className = 'trs-size-error';
    err.setAttribute('role', 'alert');
    err.textContent = 'Please select a size to continue';
    fieldset.insertAdjacentElement('afterend', err);
  }
  return err;
}

function trsSizeFieldsets() {
  return document.querySelectorAll(
    '.product--therarestreps .product-form__input--pill[data-trs-require-size], .product--therarestreps .trs-size-fieldset--required'
  );
}

function trsHasSizeSelected() {
  const fieldsets = trsSizeFieldsets();
  if (!fieldsets.length) {
    // No require-size fieldsets — treat as selected (URL variant or no size option)
    const pills = document.querySelectorAll('.product--therarestreps .product-form__input--pill');
    if (!pills.length) return true;
    let ok = true;
    pills.forEach((fs) => {
      const radios = fs.querySelectorAll('input[type="radio"]:not([disabled])');
      if (radios.length && !fs.querySelector('input[type="radio"]:checked')) ok = false;
    });
    return ok;
  }
  let ok = true;
  fieldsets.forEach((fs) => {
    const radios = fs.querySelectorAll('input[type="radio"]:not([disabled])');
    if (radios.length && !fs.querySelector('input[type="radio"]:checked')) ok = false;
  });
  return ok;
}

function trsSetAwaitingSize(awaiting) {
  document.querySelectorAll('[data-trs-awaiting-size], .product-form__submit, [data-sticky-submit]').forEach((btn) => {
    if (!btn.matches('.product-form__submit, [data-sticky-submit], [data-trs-awaiting-size]')) return;
    if (awaiting) {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('data-trs-awaiting-size', 'true');
      if (btn.matches('.product-form__submit, [data-sticky-submit]')) {
        const span = btn.querySelector('span:not(.loading__spinner):not(.svg-wrapper)') || btn;
        if (btn.matches('[data-sticky-submit]') || span === btn) {
          if (!btn.dataset.trsLabelReady) btn.dataset.trsLabelReady = btn.textContent.trim();
          btn.textContent = 'Select a size';
        } else if (span && !span.classList.contains('loading__spinner')) {
          if (!span.dataset.trsLabelReady) span.dataset.trsLabelReady = span.textContent.trim();
          span.textContent = 'Select a size';
        }
      }
      const awaitingFormId = btn.getAttribute('form');
      const awaitingScope = awaitingFormId
        ? document.getElementById(awaitingFormId) || btn.closest('product-form') || document.querySelector('product-form')
        : btn.closest('product-form') || document.querySelector('product-form');
      const idInput = awaitingScope?.querySelector('.product-variant-id');
      idInput?.setAttribute('disabled', 'disabled');
    } else {
      const wasAwaiting =
        btn.hasAttribute('data-trs-awaiting-size') || btn.getAttribute('aria-disabled') === 'true';
      btn.removeAttribute('data-trs-awaiting-size');

      const form = btn.closest('product-form') || document.querySelector('product-form');
      const idInput = form?.querySelector('.product-variant-id');
      idInput?.removeAttribute('disabled');

      if (wasAwaiting) {
        const labelText = (
          btn.querySelector('span:not(.loading__spinner):not(.svg-wrapper)')?.textContent ||
          btn.textContent ||
          ''
        ).toLowerCase();
        const soldOut = labelText.includes('sold');
        if (!soldOut) {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }
      }

      const span = btn.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
      const readyLabel =
        span?.dataset.trsLabelReady ||
        btn.dataset.trsLabelReady ||
        window.variantStrings?.addToCart ||
        'Add to cart';
      if (span) span.textContent = readyLabel;
      else if (btn.matches('[data-sticky-submit]')) btn.textContent = readyLabel;
    }
  });

  // Keep Buy it now visually/ARIA-synced with the size gate.
  // Prefer aria-disabled only — forcing [disabled] can break Shopify's accelerated checkout widget.
  document.querySelectorAll('.shopify-payment-button__button').forEach((payBtn) => {
    if (awaiting) {
      payBtn.setAttribute('aria-disabled', 'true');
    } else {
      payBtn.removeAttribute('aria-disabled');
    }
  });

  document.querySelectorAll('[data-trs-size-helper]').forEach((el) => {
    el.hidden = !awaiting;
  });
}

function trsSyncSizeGate() {
  // Always sync when size pills exist — do not bail after clearing require-size flags,
  // or the ATC button stays disabled with cursor: not-allowed.
  const hasSizePills = document.querySelectorAll('.product--therarestreps .product-form__input--pill').length > 0;
  const needsSize =
    document.querySelector('product-form[data-trs-require-size], [data-trs-require-size="true"]') ||
    document.querySelector('[data-trs-awaiting-size]');
  if (!hasSizePills && !needsSize) return;
  trsSetAwaitingSize(!trsHasSizeSelected());
}

document.addEventListener(
  'click',
  (event) => {
    const submit = event.target.closest('.product-form__submit, [data-sticky-submit]');
    const buyNow = event.target.closest(
      '.shopify-payment-button__button, shopify-accelerated-checkout, [data-shopify="payment-button"]'
    );
    if (!submit && !buyNow) return;
    if (submit?.hasAttribute('disabled')) return;

    const fieldsets = document.querySelectorAll('.product--therarestreps .product-form__input--pill');
    if (!fieldsets.length) return;

    let missing = false;
    fieldsets.forEach((fs) => {
      const radios = fs.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;
      if (!fs.querySelector('input[type="radio"]:checked')) {
        missing = true;
        fs.classList.add('trs-size-missing');
        trsEnsureSizeError(fs).classList.add('is-visible');
      } else {
        fs.classList.remove('trs-size-missing');
        const err = fs.parentElement.querySelector('.trs-size-error');
        if (err) err.classList.remove('is-visible');
      }
    });

    if (missing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const firstMissing = document.querySelector('.product-form__input--pill.trs-size-missing');
      firstMissing?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.querySelector('[data-trs-size-helper]')?.removeAttribute('hidden');
    }
  },
  true
);

document.addEventListener('change', (event) => {
  if (!event.target.matches?.('.product-form__input--pill input[type="radio"]')) return;
  const fs = event.target.closest('.product-form__input--pill');
  fs?.classList.remove('trs-size-missing');
  fs?.removeAttribute('data-trs-require-size');
  fs?.classList.remove('trs-size-fieldset--required');
  const form = document.querySelector('product-form[data-trs-require-size]');
  form?.removeAttribute('data-trs-require-size');
  const err = fs?.parentElement?.querySelector('.trs-size-error');
  if (err) err.classList.remove('is-visible');
  trsSyncSizeGate();
});

/* ---------- Feature 5: Offers & Savings — copy code (before size-gate boot; must always bind) ---------- */
function trsFallbackCopy(text, callback) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  document.body.removeChild(ta);
  if (ok && typeof callback === 'function') callback();
}

document.addEventListener(
  'click',
  (event) => {
    const btn = event.target.closest('.trs-offer-card__code[data-copy-code]');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const code = btn.getAttribute('data-copy-code') || btn.dataset.copyCode;
    if (!code) return;

    const label = btn.querySelector('.trs-offer-card__copy-label');
    const setLabel = (text) => {
      if (label) label.textContent = text;
    };
    const onCopied = () => {
      setLabel('Copied!');
      setTimeout(() => setLabel('Copy Code'), 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(onCopied).catch(() => trsFallbackCopy(code, onCopied));
    } else {
      trsFallbackCopy(code, onCopied);
    }
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trsSyncSizeGate);
} else {
  try {
    trsSyncSizeGate();
  } catch (e) {
    console.error('trsSyncSizeGate failed', e);
  }
}

/* ---------- #13 Featured review snippet (pulled from the live Judge.me widget) ---------- */
function trsInitFeaturedReview() {
  const box = document.querySelector('[data-trs-featured-review]');
  if (!box) return;

  // Try immediately in case the widget is already in the DOM.
  const existing = document.querySelector(
    '.jdgm-rev-widg .jdgm-rev, .jdgm-all-reviews__body .jdgm-rev, .jdgm-rev'
  );
  if (existing) {
    trsPopulateFeaturedReview(box, existing);
    return;
  }

  // Watch for Judge.me to inject a review element. Stop after 12 s to
  // avoid observing forever on pages with no reviews.
  const obs = new MutationObserver(() => {
    const rev = document.querySelector(
      '.jdgm-rev-widg .jdgm-rev, .jdgm-all-reviews__body .jdgm-rev, .jdgm-rev'
    );
    if (rev) {
      obs.disconnect();
      clearTimeout(obsTimeout);
      trsPopulateFeaturedReview(box, rev);
    }
  });
  const obsTarget = document.body || document.documentElement;
  obs.observe(obsTarget, { childList: true, subtree: true });
  const obsTimeout = setTimeout(function () { obs.disconnect(); }, 12000);
}

function trsPopulateFeaturedReview(box, rev) {
  const ratingEl = rev.querySelector('[data-score], .jdgm-rev__rating');
  const score = parseInt(ratingEl?.getAttribute('data-score') || '5', 10) || 5;
  const bodyText = (rev.querySelector('.jdgm-rev__body')?.textContent || '').trim();
  const author = (rev.querySelector('.jdgm-rev__author')?.textContent || '').trim();
  if (!bodyText) return;

  const starsEl = box.querySelector('[data-trs-fr-stars]');
  const bodyEl = box.querySelector('[data-trs-fr-body]');
  const authorEl = box.querySelector('[data-trs-fr-author]');
  const linkEl = box.querySelector('[data-trs-fr-link]');

  if (starsEl) starsEl.textContent = '★★★★★☆☆☆☆☆'.slice(5 - score, 10 - score);
  if (bodyEl) {
    const snippet = bodyText.length > 120 ? `${bodyText.slice(0, 120).trim()}…` : bodyText;
    bodyEl.textContent = `“${snippet}” `;
  }
  if (authorEl) authorEl.textContent = author.split(' ')[0] || author;

  const countText = (
    document.querySelector('.jdgm-prev-badge__text, .jdgm-rev-widg__summary-text')?.textContent || ''
  ).match(/\d+/);
  if (linkEl) {
    linkEl.textContent = countText ? `Read all ${countText[0]} reviews ↓` : 'Read all reviews ↓';
    linkEl.addEventListener('click', () => {
      const reviews =
        document.getElementById('judgeme_product_reviews') ||
        document.querySelector('.jdgm-rev-widg, [id^="shopify-block"] .jdgm-widget');
      reviews?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  box.hidden = false;
}

/* ---------- #9 Rating badge smooth-scrolls to reviews ---------- */
document.addEventListener('click', (event) => {
  const badge = event.target.closest('.jdgm-prev-badge');
  if (!badge) return;
  const reviews = document.getElementById('judgeme_product_reviews') ||
    document.querySelector('.jdgm-rev-widg, [id^="shopify-block"] .jdgm-widget');
  if (reviews) {
    event.preventDefault();
    reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

/* ---------- #23 Description accordion: collapsed on mobile ---------- */
function trsCollapseDescriptionOnMobile() {
  if (window.matchMedia('(max-width: 749px)').matches) {
    document
      .querySelectorAll('.trs-description-accordion details[open]')
      .forEach((d) => d.removeAttribute('open'));
  }
}

/* ---------- Size chart modal: clone Kiwi guide; normalize table to Size Guide page ---------- */
const TRS_SIZE_CHART_ROWS = [
  { uk: '3', us: '5', eu: '36', cm: '22.0', inch: '8.7' },
  { uk: '4', us: '6', eu: '37', cm: '23.0', inch: '9.1' },
  { uk: '5', us: '7', eu: '38', cm: '24.0', inch: '9.4' },
  { uk: '6', us: '8', eu: '39', cm: '25.0', inch: '9.8' },
  { uk: '7', us: '9', eu: '40', cm: '26.0', inch: '10.2' },
];

function trsActivateLazyImages(root) {
  root.querySelectorAll('img[data-src], img.lazyload').forEach((img) => {
    const src = img.getAttribute('data-src') || img.getAttribute('data-original');
    if (!src) return;
    img.src = src;
    img.removeAttribute('data-src');
    img.classList.remove('lazyload');
    img.loading = 'lazy';
  });
}

function trsEnhanceKiwiSizeTable(table) {
  if (!table || table.dataset.trsEnhanced) return;

  // Rebuild to match /pages/size-guide (whole UK sizes only — no 0.5 rows).
  table.innerHTML = `
    <thead>
      <tr>
        <td class="ks-table-cell ks-table-header-cell">UK</td>
        <td class="ks-table-cell ks-table-header-cell">US</td>
        <td class="ks-table-cell ks-table-header-cell">EU</td>
        <td class="ks-table-cell ks-table-header-cell">Foot length (cm)</td>
        <td class="ks-table-cell ks-table-header-cell">Foot length (in)</td>
      </tr>
    </thead>
    <tbody>
      ${TRS_SIZE_CHART_ROWS.map(
        (row) =>
          `<tr>
            <td class="ks-table-cell">${row.uk}</td>
            <td class="ks-table-cell">${row.us}</td>
            <td class="ks-table-cell">${row.eu}</td>
            <td class="ks-table-cell">${row.cm}</td>
            <td class="ks-table-cell">${row.inch}</td>
          </tr>`
      ).join('')}
    </tbody>
  `;

  table.dataset.trsEnhanced = 'true';
}

function trsSetSizeChartFallback(modal, showFallback) {
  const host = modal?.querySelector('[data-trs-size-chart-kiwi-host]');
  const fallback = modal?.querySelector('[data-trs-size-chart-fallback]');
  if (!host || !fallback) return;

  host.hidden = showFallback;
  host.toggleAttribute('aria-hidden', showFallback);
  fallback.hidden = !showFallback;
  fallback.toggleAttribute('aria-hidden', !showFallback);
}

function trsPopulateSizeChartKiwiHost(trigger) {
  const modalId = trigger.getAttribute('aria-controls');
  const modal = modalId ? document.getElementById(modalId) : null;
  const host = modal?.querySelector('[data-trs-size-chart-kiwi-host]');
  if (!host || !modal) return;

  const kiwiBlock = document.querySelector('.ks-modal-content-hidden .ks-chart-individual');
  host.innerHTML = '';

  if (!kiwiBlock) {
    trsSetSizeChartFallback(modal, true);
    return;
  }

  const clone = kiwiBlock.cloneNode(true);
  clone.querySelector('.ks-powered-by')?.remove();
  trsActivateLazyImages(clone);

  const htmlContent = clone.querySelector('.ks-html-content');
  const imageBlock = clone.querySelector('.ks-image');
  const tableWrapper = clone.querySelector('.ks-table-content-wrapper');
  const table = clone.querySelector('.ks-table');
  if (table) trsEnhanceKiwiSizeTable(table);

  const layout = document.createElement('div');
  layout.className = 'trs-size-chart-modal__layout';

  if (htmlContent || imageBlock) {
    const guide = document.createElement('div');
    guide.className = 'trs-size-chart-modal__guide';
    if (htmlContent) guide.appendChild(htmlContent);
    if (imageBlock) guide.appendChild(imageBlock);
    layout.appendChild(guide);
  }

  if (tableWrapper) {
    const tableSection = document.createElement('div');
    tableSection.className = 'trs-size-chart-modal__table-section';
    const heading = document.createElement('h3');
    heading.className = 'trs-size-chart-modal__section-title h4';
    heading.textContent = 'Size conversion chart';
    tableSection.appendChild(heading);
    tableSection.appendChild(tableWrapper);
    layout.appendChild(tableSection);
  }

  host.appendChild(layout);
  trsSetSizeChartFallback(modal, false);
}

function trsInitSizeChartModal() {
  if (trsInitSizeChartModal.initialized) return;
  trsInitSizeChartModal.initialized = true;

  // Delegated + capture so Kiwi content is ready before modal-opener opens the dialog.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-trs-size-chart]');
      if (!trigger) return;
      trsPopulateSizeChartKiwiHost(trigger);
    },
    true
  );
}

/* ---------- Feature 5: Offers panel boot hook (expand is native <details>) ---------- */
function trsInitOffersPanel() {
  // Copy-code listener is registered at top-level above; keep this no-op for boot callers.
  trsInitOffersPanel.initialized = true;
}

function fallbackCopy(text, callback) {
  trsFallbackCopy(text, callback);
}

/* ---------- Hide Judge.me review section when a product has no reviews ---------- */
function trsHideEmptyProductReviews() {
  if (!document.body.classList.contains('template-product')) return;

  document.querySelectorAll('.jm-review-widget, .jdgm-rev-widg').forEach((widget) => {
    const reviewCount = parseInt(widget.getAttribute('data-number-of-reviews'), 10);
    const hasReviewItems = widget.querySelector('.jdgm-rev, .jm-review, .jm-review-widget__review');
    const isEmptyState = widget.querySelector('.jm-no-reviews-state, .jdgm-empty-state');
    const shouldHide =
      isEmptyState ||
      widget.getAttribute('data-number-of-reviews') === '0' ||
      (Number.isFinite(reviewCount) && reviewCount === 0 && !hasReviewItems);

    if (!shouldHide || hasReviewItems) return;

    const section = widget.closest('.shopify-section');
    if (section) section.style.display = 'none';
  });
}

/* ---------- FIX5: tag reviews section so the anchor #product-reviews works ---------- */
function trsTagReviewsSection() {
  const reviews =
    document.getElementById('judgeme_product_reviews') ||
    document.querySelector('.jdgm-rev-widg, .jdgm-widget, [id^="shopify-block"] .jdgm-widget');
  if (reviews && !reviews.id) {
    reviews.id = 'product-reviews';
  } else if (reviews && reviews.id !== 'product-reviews') {
    reviews.setAttribute('id', 'product-reviews');
  }
  // Also handle the FIX5 anchor scroll for sticky header offset
  document.querySelectorAll('a[href="#product-reviews"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const target = document.getElementById('product-reviews');
      if (!target) return;
      e.preventDefault();
      const stickyBar = document.querySelector('.shopify-section-header-sticky, header.header');
      const offset = stickyBar ? stickyBar.offsetHeight + 16 : 80;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
}

/* ---------- FIX4: quick-add overlay AJAX handler ---------- */
function trsInitCardQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-trs-quick-add]');
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (res.ok) {
        btn.textContent = '✓ Added';
        btn.classList.add('trs-card-quick-add--success');
        // Refresh cart bubble count
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(() => {
          btn.textContent = originalText;
          btn.classList.remove('trs-card-quick-add--success');
          btn.disabled = false;
        }, 1500);
      } else {
        throw new Error('Add failed');
      }
    } catch {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  });
}

/* ---------- Keep desktop gallery controls pinned to the image box ---------- */
function trsInitGalleryControlBounds() {
  const galleryViewer = document.querySelector('[id^="GalleryViewer-"]');
  if (!galleryViewer) return;

  let frame = null;

  const updateBounds = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const activeSlide =
        galleryViewer.querySelector('.product__media-item.is-active') ||
        galleryViewer.querySelector('.product__media-item');
      const mediaBox = activeSlide?.querySelector('.product-media-container, .media');
      const controls = galleryViewer.querySelector(':scope > .slider-buttons');
      if (!activeSlide || !mediaBox || !controls) return;

      const rootRect = galleryViewer.getBoundingClientRect();
      const mediaRect = mediaBox.getBoundingClientRect();

      galleryViewer.style.setProperty('--trs-gallery-left', `${Math.max(0, mediaRect.left - rootRect.left)}px`);
      galleryViewer.style.setProperty('--trs-gallery-top', `${Math.max(0, mediaRect.top - rootRect.top)}px`);
      galleryViewer.style.setProperty('--trs-gallery-width', `${mediaRect.width}px`);
      galleryViewer.style.setProperty('--trs-gallery-height', `${mediaRect.height}px`);
    });
  };

  updateBounds();
  const debouncedResize = trsDebounce(updateBounds, 150);
  window.addEventListener('resize', debouncedResize);
  galleryViewer.addEventListener('scroll', updateBounds, { passive: true });
  galleryViewer.addEventListener('click', () => requestAnimationFrame(updateBounds));
  document.addEventListener('shopify:section:load', updateBounds);
  document.addEventListener('variant:change', updateBounds);

  if (window.MutationObserver) {
    new MutationObserver(updateBounds).observe(galleryViewer, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'aria-current'],
    });
  }
}

/*
  Wishlist by Square owns and repeatedly remounts its own product heart.
  Never display or move that node: it is the source of the gallery flicker.
  Instead, render a stable theme-owned button and delegate its click to the
  hidden Vital button. Vital still owns the wishlist state and persistence.
*/
function trsPinGalleryWishlist() {
  const gallery = document.querySelector('.product--therarestreps [id^="GalleryViewer-"]');
  if (!gallery || gallery.dataset.trsWishlistPinned === '1') return;
  gallery.dataset.trsWishlistPinned = '1';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'trs-gallery-wishlist';
  button.setAttribute('data-trs-gallery-wishlist', '');
  button.setAttribute('aria-label', 'Add to wishlist');
  button.setAttribute('aria-pressed', 'false');
  button.innerHTML =
    '<svg aria-hidden="true" viewBox="0 0 20 20" focusable="false">' +
    '<path d="M10 17.495 2.305 9.901a4.293 4.293 0 0 1 0-6.128 4.433 4.433 0 0 1 6.21 0L10 5.24l1.485-1.467a4.433 4.433 0 0 1 6.21 0 4.293 4.293 0 0 1 0 6.128L10 17.495Z"></path>' +
    '</svg>';

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const vitalButton = document.querySelector(
      '.product--therarestreps .Vtl-WishlistButton__WishlistIconButton'
    );
    if (!vitalButton) return;
    vitalButton.click();
    window.setTimeout(syncState, 100);
  });

  const syncState = () => {
    const vitalButton = document.querySelector(
      '.product--therarestreps .Vtl-WishlistButton__WishlistIconButton'
    );
    const isSaved = /remove|added|go to/i.test(
      `${vitalButton?.getAttribute('title') || ''} ${vitalButton?.textContent || ''}`
    );
    button.classList.toggle('is-saved', isSaved);
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute('aria-label', isSaved ? 'Remove from wishlist' : 'Add to wishlist');
  };

  gallery.appendChild(button);
  trsProtectCornerPointer(button);
  syncState();

  if (window.MutationObserver) {
    new MutationObserver(syncState).observe(document.body, { childList: true, subtree: true });
  }
}

/* Stop scroll/swipe gestures from stealing taps on corner controls */
function trsProtectCornerPointer(el) {
  if (!el || el.dataset.trsCornerProtect === '1') return;
  el.dataset.trsCornerProtect = '1';
  ['pointerdown', 'touchstart', 'mousedown', 'touchmove'].forEach((type) => {
    el.addEventListener(
      type,
      (e) => {
        e.stopPropagation();
      },
      { passive: true }
    );
  });
}

/*
  Pin a zoom button onto GalleryViewer (outside the scrollable media list).
  In-slide .product__media-icon sits inside the slider track, so slight
  finger/mouse movement becomes a horizontal swipe instead of a click.
*/
function trsPinGalleryZoom() {
  const gallery = document.querySelector('.product--therarestreps [id^="GalleryViewer-"]');
  if (!gallery || gallery.querySelector('[data-trs-gallery-zoom]')) return;

  const sourceIcon = gallery.querySelector('.product__media-icon');
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'trs-gallery-zoom';
  btn.setAttribute('data-trs-gallery-zoom', '');
  btn.setAttribute('aria-label', 'Zoom image');
  btn.innerHTML = sourceIcon ? sourceIcon.innerHTML : '';

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const toggle = gallery.querySelector(
      '.product__media-item.is-active .product__media-toggle'
    );
    if (!toggle) return;
    const opener = toggle.closest('modal-opener');
    const modal =
      opener && document.querySelector(opener.getAttribute('data-modal'));
    /* Prefer modal.show() so we don't synthesize a click that GalleryViewer
       edge-nav can intercept (clientX=0 → left edge → previous slide). */
    if (modal && typeof modal.show === 'function') {
      modal.show(toggle);
    } else {
      toggle.click();
    }
  });

  trsProtectCornerPointer(btn);
  gallery.appendChild(btn);
}

function trsBootProductPage() {
  trsCollapseDescriptionOnMobile();
  trsInitFeaturedReview();
  trsInitOffersPanel();
  trsInitSizeChartModal();
  trsInitCardQuickAdd();
  trsInitGalleryControlBounds();
  trsPinGalleryZoom();
  trsPinGalleryWishlist();
  // FIX5: try immediately; retry after a delay for lazy-loaded reviews
  trsTagReviewsSection();
  trsHideEmptyProductReviews();
  setTimeout(() => {
    trsTagReviewsSection();
    trsHideEmptyProductReviews();
    trsPinGalleryZoom();
    trsPinGalleryWishlist();
  }, 2000);

  // Thumbnail carousel arrow fix.
  // Root cause: SliderComponent registers its click listener on the button in its constructor
  // (before DOMContentLoaded + rAF + rAF). At the TARGET element, all listeners fire in
  // registration order regardless of capture flag — so adding capture=true on the BUTTON
  // still fires SECOND (SliderComponent already ran, corrupting scroll with NaN).
  // Fix: register capture on the ANCESTOR (thumbSliderEl). During the capture phase,
  // ancestor listeners fire before the event reaches the target, so we intercept first.
  requestAnimationFrame(() => {
    const thumbSliderEl = document.querySelector('[id^="GalleryThumbnails-"]');
    if (!thumbSliderEl) return;
    const thumbUl = thumbSliderEl.querySelector('[id^="Slider-Thumbnails-"]');
    if (!thumbUl) return;

    if (typeof thumbSliderEl.initPages === 'function') thumbSliderEl.initPages();

    thumbSliderEl.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="previous"], button[name="next"]');
      if (!btn) return;
      // Stop propagation so SliderComponent's button listener never fires
      e.stopPropagation();
      e.preventDefault();
      const visible = [...thumbUl.querySelectorAll('[id^="Slide-"]')].filter((el) => el.offsetWidth > 0);
      if (!visible.length) return;
      const itemW = visible[0].offsetWidth;
      const gap = parseFloat(getComputedStyle(thumbUl).columnGap) || 12.8;
      const step = parseInt(btn.dataset.step, 10) || 3;
      const delta = btn.name === 'next' ? step * (itemW + gap) : -step * (itemW + gap);
      thumbUl.scrollBy({ left: delta, behavior: 'smooth' });
      setTimeout(() => { if (typeof thumbSliderEl.update === 'function') thumbSliderEl.update(); }, 400);
    }, true); // capture on ANCESTOR — fires during capture phase, BEFORE button's own listener list
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', trsBootProductPage);
} else {
  trsBootProductPage();
}
